import { gql } from '@apollo/client';
import React from 'react';
import classNames from 'classnames';
import { PageFilterFragment } from '../../generated/types/PageFilterFragment';
import { PageFilterFiltersFragment } from '../../generated/types/PageFilterFiltersFragment';
import { GuidePageQuery_displayPage as DisplayPage } from '../../generated/types/GuidePageQuery';
import { CATEGORY_TREE_FRAGMENT } from '../../hooks/useCategoryTree';
import {
  PageFilterAvailability,
  PAGE_FILTER_AVAILABILITY_FACET_FRAGMENT
} from './PageFilterAvailability';
import { PageFilterCategory } from './PageFilterCategory';
import { PageFilterPages } from './PageFilterPages';
import { PageFilterFacets } from './PageFilterFacets';
import {
  PAGE_FILTER_FACET_FRAGMENT,
  PAGE_FILTER_NAME_SLUG_FACET_FRAGMENT,
  PAGE_FILTER_SIZE_FACET_FRAGMENT
} from './PageFilterFacet';
import { PageFilterPrice } from './PageFilterPrice';
import './PageFilter.scss';

export const PAGE_FILTER_FRAGMENT = gql`
  fragment PageFilterFragment on CollectionResult {
    ...CategoryTreeFragment
  }
  ${CATEGORY_TREE_FRAGMENT}
`;

export const PAGE_FILTER_FILTERS_FRAGMENT = gql`
  fragment PageFilterFiltersFragment on CollectionResult {
    attributes {
      ...PageFilterNameSlugFacetFragment
    }
    availability {
      ...PageFilterAvailabilityFacetFragment
    }
    filterColors: filter_colors {
      ...PageFilterFacetFragment
    }
    designers {
      ...PageFilterNameSlugFacetFragment
    }
    sizes {
      ...PageFilterSizeFacetFragment
    }
  }
  ${PAGE_FILTER_AVAILABILITY_FACET_FRAGMENT}
  ${PAGE_FILTER_FACET_FRAGMENT}
  ${PAGE_FILTER_NAME_SLUG_FACET_FRAGMENT}
  ${PAGE_FILTER_SIZE_FACET_FRAGMENT}
`;

interface PageFilterProps extends React.HTMLAttributes<HTMLDivElement> {
  attributes: PageFilterFiltersFragment['attributes'];
  availability: PageFilterFiltersFragment['availability'];
  categoryTree: PageFilterFragment['categoryTree'];
  colors: PageFilterFiltersFragment['filterColors'];
  designers: PageFilterFiltersFragment['designers'];
  mobile?: boolean;
  onClose: () => void;
  sizes: PageFilterFiltersFragment['sizes'];
  showPriceFilter?: boolean;
  showCounts?: boolean;
  guideSubPages?: DisplayPage['guideSubPages'];
}

export const PageFilter: React.FC<PageFilterProps> = ({
  attributes,
  availability,
  categoryTree,
  className,
  colors,
  designers,
  mobile,
  onClose,
  sizes,
  showPriceFilter = true,
  showCounts,
  guideSubPages,
  ...rest
}) => {
  return (
    <div className={classNames('PageFilter', className)} {...rest}>
      <PageFilterAvailability
        availability={availability}
        mobile={mobile}
        showCounts={showCounts}
        onClose={onClose}
      />
      {guideSubPages && guideSubPages.length > 0 && (
        <PageFilterPages guideSubPages={guideSubPages} mobile={mobile} onClose={onClose} />
      )}
      <PageFilterCategory
        attributes={attributes}
        categoryTree={categoryTree}
        mobile={mobile}
        showCounts={showCounts}
        onClose={onClose}
      />
      <PageFilterFacets
        expandable
        facets={designers}
        facetName="designers"
        sortingBy={facet => facet.name as string}
        mobile={mobile}
        showCounts={showCounts}
        onClose={onClose}
        searchable
        title="Designer"
        virtual
      />
      <PageFilterFacets
        expandable
        facets={sizes}
        facetName="sizes"
        mobile={mobile}
        showCounts={showCounts}
        onClose={onClose}
        title="Size"
        type="button"
        virtual
      />
      <PageFilterFacets
        expandable
        facets={colors}
        facetName="colors"
        sortingBy={facet => facet.name as string}
        mobile={mobile}
        showCounts={showCounts}
        onClose={onClose}
        title="Color"
        virtual
      />
      {showPriceFilter && <PageFilterPrice mobile={mobile} onClose={onClose} />}
    </div>
  );
};
